import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Send, Building2, Mail, Phone, Globe, MessageSquare, CheckCircle2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import Header from './Header';
import Footer from './Footer';

const API = process.env.REACT_APP_BACKEND_URL;

const WhatsAppIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const ContactPage = () => {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    name: '', email: '', company: '', phone: '',
    interest: 'enterprise', message: '', sites_count: ''
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [whatsapp, setWhatsapp] = useState({ number: '', message: '' });

  useEffect(() => {
    fetch(`${API}/api/settings/public`)
      .then(r => r.json())
      .then(data => setWhatsapp({
        number: data.whatsapp_number || '',
        message: data.whatsapp_message || ''
      }))
      .catch(() => {});
  }, []);

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.name || !form.email || !form.message) {
      setError(t('contact.errors.required'));
      return;
    }
    setLoading(true);
    if (form.message.trim().length < 10) {
      setError(t('contact.errors.minLength'));
      setLoading(false);
      return;
    }
    try {
      const res = await fetch(`${API}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      if (res.status === 400) {
        setError(t('contact.errors.badRequest'));
      } else if (!res.ok) {
        setError(t('contact.errors.generic', { code: res.status }));
      } else {
        setSent(true);
      }
    } catch (err) {
      const msg = err && err.message ? err.message : t('contact.errors.network');
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const whatsappUrl = whatsapp.number
    ? `https://wa.me/${whatsapp.number.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(whatsapp.message)}`
    : null;

  const interests = [
    { value: 'enterprise', label: t('contact.form.interests.enterprise') },
    { value: 'whitelabel', label: t('contact.form.interests.whitelabel') },
    { value: 'plugin',     label: t('contact.form.interests.plugin') },
    { value: 'custom',     label: t('contact.form.interests.custom') },
    { value: 'other',      label: t('contact.form.interests.other') },
  ];

  if (sent) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6" data-testid="contact-success">
          <div className="max-w-lg text-center animate-fade-up">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[#10B981]/10 border border-[#10B981]/30 mb-8">
              <CheckCircle2 className="w-10 h-10 text-[#10B981]" />
            </div>
            <h1 className="font-['Outfit'] text-4xl font-medium mb-4">{t('contact.success.title')}</h1>
            <p className="text-[#A1A1AA] text-lg mb-8">{t('contact.success.body')}</p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/">
                <Button className="bg-[#0055FF] hover:bg-[#3377FF] text-white rounded-full px-8 py-6 text-lg" data-testid="contact-back-home">
                  {t('contact.success.backHome')}
                </Button>
              </Link>
              {whatsappUrl && (
                <a href={whatsappUrl} target="_blank" rel="noopener noreferrer">
                  <Button className="bg-[#25D366] hover:bg-[#20BD5A] text-white rounded-full px-8 py-6 text-lg" data-testid="contact-success-whatsapp">
                    <WhatsAppIcon className="w-5 h-5 mr-2" />
                    {t('contact.success.whatsappBtn')}
                  </Button>
                </a>
              )}
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="min-h-screen bg-[#050505] pt-28 pb-20 px-6" data-testid="contact-page">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16 animate-fade-up">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#0055FF]/10 border border-[#0055FF]/30 mb-6">
              <Building2 className="w-4 h-4 text-[#0055FF]" />
              <span className="text-sm text-[#A1A1AA]">{t('contact.header.badge')}</span>
            </div>
            <h1 className="font-['Outfit'] text-4xl sm:text-5xl font-medium tracking-tight mb-4">{t('contact.header.title')}</h1>
            <p className="text-lg text-[#A1A1AA] max-w-2xl mx-auto">{t('contact.header.subtitle')}</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 lg:gap-12 items-start">
            {/* Left - Info Cards */}
            <div className="lg:col-span-2 space-y-6 animate-fade-up">
              {/* WhatsApp CTA - prominent */}
              {whatsappUrl && (
                <a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block p-6 rounded-2xl bg-[#25D366]/10 border border-[#25D366]/30 hover:border-[#25D366]/60 transition-all group"
                  data-testid="whatsapp-cta"
                >
                  <div className="flex items-center gap-4">
                    <div className="p-3 rounded-xl bg-[#25D366] group-hover:scale-110 transition-transform">
                      <WhatsAppIcon className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-['Outfit'] font-medium text-lg text-white mb-1">{t('contact.info.whatsapp.title')}</h3>
                      <p className="text-sm text-[#A1A1AA]">{t('contact.info.whatsapp.desc')}</p>
                    </div>
                    <svg className="w-5 h-5 text-[#25D366] group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7"/></svg>
                  </div>
                </a>
              )}

              <div className="p-6 rounded-2xl bg-[#111111] border border-white/5">
                <h3 className="font-['Outfit'] font-medium text-lg mb-4">{t('contact.info.whyEnterprise.title')}</h3>
                <ul className="space-y-4">
                  {[
                    { icon: Globe,         text: t('contact.info.whyEnterprise.item1') },
                    { icon: Building2,     text: t('contact.info.whyEnterprise.item2') },
                    { icon: MessageSquare, text: t('contact.info.whyEnterprise.item3') },
                  ].map(({ icon: Icon, text }, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <div className="mt-0.5 p-1.5 rounded-lg bg-[#0055FF]/10">
                        <Icon className="w-4 h-4 text-[#0055FF]" />
                      </div>
                      <span className="text-[#A1A1AA] text-sm leading-relaxed">{text}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="p-6 rounded-2xl bg-[#111111] border border-white/5">
                <h3 className="font-['Outfit'] font-medium text-lg mb-4">{t('contact.info.directContacts.title')}</h3>
                <div className="space-y-3">
                  <a href="mailto:enterprise@seo-orchestra.ai" className="flex items-center gap-3 text-[#A1A1AA] hover:text-white transition-colors text-sm">
                    <Mail className="w-4 h-4 text-[#0055FF]" />
                    enterprise@seo-orchestra.ai
                  </a>
                  {whatsapp.number && (
                    <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-[#A1A1AA] hover:text-white transition-colors text-sm">
                      <WhatsAppIcon className="w-4 h-4 text-[#25D366]" />
                      {whatsapp.number}
                    </a>
                  )}
                  <div className="flex items-center gap-3 text-[#A1A1AA] text-sm">
                    <Building2 className="w-4 h-4 text-[#0055FF]" />
                    {t('contact.info.directContacts.address')}
                  </div>
                </div>
              </div>

              <div className="p-6 rounded-2xl bg-gradient-to-br from-[#0055FF]/10 to-transparent border border-[#0055FF]/20">
                <p className="text-sm text-[#A1A1AA] leading-relaxed" dangerouslySetInnerHTML={{ __html: t('contact.info.replyGuarantee') }} />
              </div>
            </div>

            {/* Right - Contact Form */}
            <div className="lg:col-span-3 animate-fade-up" style={{ animationDelay: '100ms' }}>
              <form onSubmit={handleSubmit} className="p-8 rounded-2xl bg-[#111111] border border-white/5" data-testid="contact-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
                  <div>
                    <label className="block text-sm text-[#A1A1AA] mb-2">{t('contact.form.nameLabel')} *</label>
                    <input
                      type="text" name="name" value={form.name} onChange={handleChange} required
                      className="w-full px-4 py-3 rounded-xl bg-[#0a0a0a] border border-white/10 text-white text-sm focus:border-[#0055FF] focus:outline-none transition-colors"
                      placeholder={t('contact.form.namePlaceholder')}
                      data-testid="contact-name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-[#A1A1AA] mb-2">{t('contact.form.emailLabel')} *</label>
                    <input
                      type="email" name="email" value={form.email} onChange={handleChange} required
                      className="w-full px-4 py-3 rounded-xl bg-[#0a0a0a] border border-white/10 text-white text-sm focus:border-[#0055FF] focus:outline-none transition-colors"
                      placeholder={t('contact.form.emailPlaceholder')}
                      data-testid="contact-email"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
                  <div>
                    <label className="block text-sm text-[#A1A1AA] mb-2">{t('contact.form.companyLabel')}</label>
                    <input
                      type="text" name="company" value={form.company} onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl bg-[#0a0a0a] border border-white/10 text-white text-sm focus:border-[#0055FF] focus:outline-none transition-colors"
                      placeholder={t('contact.form.companyPlaceholder')}
                      data-testid="contact-company"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-[#A1A1AA] mb-2">{t('contact.form.phoneLabel')}</label>
                    <input
                      type="tel" name="phone" value={form.phone} onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl bg-[#0a0a0a] border border-white/10 text-white text-sm focus:border-[#0055FF] focus:outline-none transition-colors"
                      placeholder="+39 333 000 0000"
                      data-testid="contact-phone"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
                  <div>
                    <label className="block text-sm text-[#A1A1AA] mb-2">{t('contact.form.interestLabel')}</label>
                    <select
                      name="interest" value={form.interest} onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl bg-[#0a0a0a] border border-white/10 text-white text-sm focus:border-[#0055FF] focus:outline-none transition-colors appearance-none"
                      data-testid="contact-interest"
                    >
                      {interests.map(i => (
                        <option key={i.value} value={i.value}>{i.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-[#A1A1AA] mb-2">{t('contact.form.sitesLabel')}</label>
                    <select
                      name="sites_count" value={form.sites_count} onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl bg-[#0a0a0a] border border-white/10 text-white text-sm focus:border-[#0055FF] focus:outline-none transition-colors appearance-none"
                      data-testid="contact-sites"
                    >
                      <option value="">{t('contact.form.sitesSelect')}</option>
                      <option value="1-5">{t('contact.form.sites15')}</option>
                      <option value="6-20">{t('contact.form.sites620')}</option>
                      <option value="21-50">{t('contact.form.sites2150')}</option>
                      <option value="50+">{t('contact.form.sites50plus')}</option>
                    </select>
                  </div>
                </div>

                <div className="mb-6">
                  <label className="block text-sm text-[#A1A1AA] mb-2">{t('contact.form.messageLabel')} *</label>
                  <textarea
                    name="message" value={form.message} onChange={handleChange} required rows={5}
                    className="w-full px-4 py-3 rounded-xl bg-[#0a0a0a] border border-white/10 text-white text-sm focus:border-[#0055FF] focus:outline-none transition-colors resize-none"
                    placeholder={t('contact.form.messagePlaceholder')}
                    data-testid="contact-message"
                  />
                </div>

                {error && (
                  <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm" data-testid="contact-error">
                    {error}
                  </div>
                )}

                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    type="submit"
                    disabled={loading}
                    className="flex-1 bg-[#0055FF] hover:bg-[#3377FF] text-white rounded-xl py-6 text-base font-medium"
                    data-testid="contact-submit"
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        {t('contact.form.submitting')}
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <Send className="w-4 h-4" />
                        {t('contact.form.submit')}
                      </span>
                    )}
                  </Button>

                  {whatsappUrl && (
                    <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="flex-shrink-0">
                      <Button
                        type="button"
                        className="w-full sm:w-auto bg-[#25D366] hover:bg-[#20BD5A] text-white rounded-xl py-6 px-6 text-base font-medium"
                        data-testid="contact-whatsapp-btn"
                      >
                        <WhatsAppIcon className="w-5 h-5 mr-2" />
                        WhatsApp
                      </Button>
                    </a>
                  )}
                </div>

                <p className="mt-4 text-xs text-center text-[#555]">
                  {t('contact.form.acceptHint')} <Link to="/privacy" className="text-[#0055FF] hover:underline">Privacy Policy</Link>
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default ContactPage;
